import { HttpResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ISequence, SequenceTypes } from '@tacliatech/types';
import { AmplitudeService } from '@web-frontend/shared/amplitude.service';
import { SequenceService } from '@web-frontend/shared/services/sequence';
import { ToastService } from '@web-frontend/shared/services/toast/toast.service';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { VideoService } from '../modal-video/video.service';
import { ErrorHandlerService } from '@web-frontend/shared/services/error';

@Component({
  selector: 'roma-create-sequence',
  templateUrl: './create-sequence.component.html',
  styleUrls: ['./create-sequence.component.scss'],
})
export class CreateSequenceComponent implements OnInit {
  constructor(
    private dialogRef: MatDialogRef<CreateSequenceComponent>,
    private sequenceService: SequenceService,
    private changeDetectorRef: ChangeDetectorRef,
    private toastService: ToastService,
    private i18n: TranslateService,
    private amplitudeService: AmplitudeService,
    private video: VideoService,
    private readonly errorHandlerService: ErrorHandlerService,
    @Inject(MAT_DIALOG_DATA)
    public data?: ISequence & { disableBudgetList?: boolean }
  ) {}

  @Input()
  showIncrement = false;

  @ViewChild('rowContainer', { static: false }) rowContainer: ElementRef;

  sub$ = new Subscription();
  types: SequenceTypes[] = Object.values(SequenceTypes) as SequenceTypes[];
  sequenceTypeRef = SequenceTypes;
  isSubmitting = false;
  modifiedSequenceId: string;
  sequence: ISequence;
  submitError = false;
  disableList = false;

  prefixError: string;

  numberPattern = /^[0-9]+$/;
  alphanumericPattern = /[a-zA-Z0-9]$/;

  ngOnInit(): void {
    if (this.data) {
      this.sequence = { ...this.data };
      this.disableList = this.data.disableBudgetList
        ? this.data.disableBudgetList
        : false;
    } else {
      this.sequence = {
        name: '',
        type: SequenceTypes.BILL,
        incrementValue: 1,
        currentNumber: 1,
        prefix: '',
      };
    }

    this.draw();
  }

  private draw() {
    this.changeDetectorRef.detectChanges();
  }

  onPaste(event: ClipboardEvent) {
    const pastedData = event.clipboardData.getData('text');
    const isNumber = this.numberPattern.test(pastedData);
    return isNumber;
  }

  onChangeCurrentNumber(): void {
    if (this.sequence.currentNumber <= 0) {
      this.sequence.currentNumber = 1;
    }
  }

  getLang(): string {
    return this.i18n.currentLang;
  }

  alignActionStyle(): string {
    return this.tutorialExists() ? 'space-between' : 'flex-end';
  }

  tutorialExists(): boolean {
    return this.getLang() === 'es';
  }

  openTutorialVideo(): void {
    this.video.open('serial-numbers', {});
  }

  validatePrefix(): boolean {
    this.prefixError = undefined;

    if (!this.sequence.prefix) {
      this.prefixError = 'sequence.prefixRequired';
    } else if (!this.alphanumericPattern.test(this.sequence.prefix)) {
      this.prefixError = 'sequence.prefixAlphanumeric';
    }

    return !this.prefixError;
  }

  submit() {
    this.submitError = false;
    this.isSubmitting = true;

    const isValid = this.validatePrefix();
    if (!this.sequence?.name || !isValid) {
      this.submitError = true;
      this.isSubmitting = false;
      return;
    }

    const obs$ = this.sequence?.id
      ? this.sequenceService.editSequence(this.sequence)
      : this.sequenceService.addSequence(this.sequence);

    const text = this.sequence?.id
      ? 'sequence.updatedSucces'
      : 'sequence.createdSucces';

    this.sub$.add(
      obs$
        .pipe(
          finalize(() => {
            // Send amplitude event
            this.amplitudeService.sendEvent({
              event: 'settings_addSerie_add',
            });
          })
        )
        .subscribe(
          (res: HttpResponse<ISequence>) => {
            this.isSubmitting = false;
            this.modifiedSequenceId =
              this.sequence.id || (res.body as ISequence).id;
            this.toastService.show({
              msDuration: 2000,
              type: 'success',
              text: text,
            });
            this.close(true);
          },
          (error) => {
            this.isSubmitting = false;
            this.errorHandlerService.handleBackendErrors(error);
          }
        )
    );
  }

  getTranslationBySequenceType(sequenceType: string) {
    if (sequenceType === SequenceTypes.ADD_BUDGETS) {
      return this.i18n.instant('sequence.types.allBudgets');
    }
    return this.i18n.instant(`sequence.types.${sequenceType.toLowerCase()}`);
  }

  getPreviewDocumentType(): string {
    if (!this.sequence?.type) {
      return this.getTranslationBySequenceType(SequenceTypes.BILL);
    }
    return this.getTranslationBySequenceType(this.sequence.type);
  }

  getPreviewNumber(): string {
    if (!this.sequence?.prefix || !this.sequence?.currentNumber) {
      return '--';
    }
    const prefix = this.sequence?.prefix;
    const currentNumber = this.sequence?.currentNumber;
    return `${prefix}-${currentNumber}`;
  }

  onSelectOpen(): void {
    if (this.rowContainer) {
      setTimeout(() => {
        this.rowContainer.nativeElement.scrollTo({
          top: this.rowContainer.nativeElement.scrollHeight,
          behavior: 'smooth',
        });
      }, 100);
    }
  }

  close(e?: boolean) {
    this.dialogRef.close(this.modifiedSequenceId);
    this.isSubmitting = false;
  }

  onKeyPress(event: KeyboardEvent): void {
    const keyCode = '';
    const key = event.key;

    // Asegurarse de que solo se permitan números
    if (!/^\d$/.test(key)) {
      event.preventDefault();
    }
  }
}
